import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, {
  forwardRef,
  startTransition,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';

export type NameCellProps = {
  label: string;
  name: string;
  isReadOnly: boolean;
  onValidityChange?: (isValid: boolean) => void;
}

export type NameCellHandle = {
  getValue: () => string;
  reset: (value: string) => void;
}

const NameCell = forwardRef<NameCellHandle, Readonly<NameCellProps>>(
  function NameCell(props, forwardedRef) {
    const [localName, setLocalName] = useState<string>(props.name);
    const prevValid = useRef<boolean>(localName?.trim().length > 0);

    useEffect(() => {
      const nextValid = props.name?.trim().length > 0;
      if (prevValid.current !== nextValid) {
        props.onValidityChange?.(nextValid);
        prevValid.current = nextValid;
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.name, props.onValidityChange]);

    useImperativeHandle(forwardedRef, () => ({
      getValue: () => localName,
      reset: (value: string) => {
        setLocalName(value);
        const nextValid = value.trim().length > 0;
        if (prevValid.current !== nextValid) {
          props.onValidityChange?.(nextValid);
          prevValid.current = nextValid;
        }
      }
    }));

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.currentTarget.value;
      setLocalName(v);
      const nextValid = v.trim().length > 0;
      if (prevValid.current !== nextValid) {
        startTransition(() => props.onValidityChange?.(nextValid));
        prevValid.current = nextValid;
      }
    };

    if (props.isReadOnly) {
      return (
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {props.name}
        </Typography>
      );
    }

    return (
      <TextField
        variant="standard"
        fullWidth
        required
        label={props.label}
        error={localName?.trim().length === 0}
        value={localName}
        onChange={handleChange}
      />
    )
  });

export default React.memo(NameCell);
